import { FormikValues } from 'formik';
import React, { useEffect, useRef, useState } from 'react';

import { Card } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';

import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import InlineSelect from '../../../common/inline/InlineSelect';
import InlineHtml from '../../../common/inline/InlineHtml';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import { FetchError } from '../../../common/api/api';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import {
  BaseHazardCardProps,
  Hazard,
  HazardCategoryOptions
} from '../Hazard.interface';
import { useUpdateHazard } from '../Hazard.hooks';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { getHazardIdPrefix } from '../Hazard.config';

const HazardInfoCard: React.FC<BaseHazardCardProps> = props => {
  const { hazard, hazardVersion, onChange } = props;
  const [error, setError] = useState<FetchError | undefined>();
  const formikRef = useRef<FormikValues>();
  const onChangeRef = useRef(onChange);
  const { updatedHazard, isUpdating, updateError, updateHazard } =
    useUpdateHazard(hazard.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(hazard.task.id || 0);
  const { isFieldDisabled } = useTaskEntityFields(hazard.task);
  const disabled = !!hazardVersion;
  const data = hazardVersion || hazard;

  useEffect(() => {
    if (updatedHazard) {
      onChangeRef.current?.(updatedHazard);
    }
  }, [updatedHazard]);
  useEffect(() => {
    if (updatedTask) {
      hazard.task = Object.assign(hazard.task || {}, updatedTask);
      onChange?.(hazard);
    }
  }, [updatedTask]);
  useEffect(() => {
    setError(updateError || updateTaskError);
  }, [updateError, updateTaskError]);

  return (
    <Card>
      <CardTitle>
        <DescriptionIcon />
        Hazard Details
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          error={error}
          isSubmitting={isUpdating}
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={{
            id: data.id || 0,
            title: data.title || '',
            category: data.category || '',
            otherCategory: data.otherCategory || ''
          }}
          onSubmit={(dto: Partial<Hazard>) => updateHazard(dto)}
        >
          <InlineForm>
            <InlineTextField
              disabled
              name='id'
              label='Hazard Identifier'
              placeholder='Enter Hazard Identifier'
              value={`${getHazardIdPrefix()}-${hazard.id}`}
            />
            <InlineTextField
              name='title'
              label='Hazardous Situation'
              placeholder='Enter Hazardous Situation'
              disabled={disabled || isFieldDisabled('title')}
              helperText='Circumstance in which people, property, or the environment are exposed to one or more hazard(s)'
            />
            <InlineSelect
              name='category'
              label='Hazard Category'
              placeholder='Selct Hazard Category'
              disabled={disabled || isFieldDisabled('category')}
              options={HazardCategoryOptions}
            />
            <InlineHtml
              name='otherCategory'
              label='Other Hazard Category'
              helperText='Use this field if "Other" was selected for the Hazard Category above'
              disabled={
                disabled || isUpdating || isFieldDisabled('otherCategory')
              }
              attachments={data.task.attachments || []}
              onChangeAttachments={(attachments: InlineFileValue[]) => {
                updateTask({ attachments });
                if (hazard.task) {
                  hazard.task.attachments = attachments;
                }
                onChange?.(hazard);
              }}
            />
          </InlineForm>
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default HazardInfoCard;
